import {
  IAccountingFinalizationGroupStat,
  IAccountingFinalizationTimeStats,
  IAreaDeliveryTimeStat,
  IAreaDeliveryTimeStats,
  ICancellationStats,
  IDriverSearchGroupStat,
  IDriverSearchTimeStats,
  IModifiedOrderAcceptanceTimeAreaStat,
  IModifiedOrderAcceptanceTimeStats,
  IOrderCycleTimeDataQualityStat,
  IOrderCycleTimeStats,
  IOrderOperationsAnalytics,
  IOrderOperationsAreaStat,
  IOrderOperationsDataQualityStat,
  IOrderOperationsStage,
  IPreparingTimeAreaStat,
  IPreparingTimeStats,
  IStoreAcceptanceTimeStat,
  IStoreAcceptanceTimeStats,
} from "../models/statistcs/respons";

export interface IOrderOperationsMappedStats {
  storeAcceptance?: IStoreAcceptanceTimeStats;
  preparing?: IPreparingTimeStats;
  customerAcceptance?: IModifiedOrderAcceptanceTimeStats;
  accountingFinalization?: IAccountingFinalizationTimeStats;
  driverSearch?: IDriverSearchTimeStats;
  delivery?: IAreaDeliveryTimeStats;
  orderCycle?: IOrderCycleTimeStats;
  customerCancellation?: ICancellationStats;
  administrativeCancellation?: ICancellationStats;
}

const extra = (stage?: IOrderOperationsStage) => stage?.extraMetrics || {};

const nullableNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const countNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const hasOwn = (source: Record<string, any> | undefined, key: string) =>
  !!source && Object.prototype.hasOwnProperty.call(source, key);

const metricValue = (
  source: Record<string, any>,
  preferredKey: string,
  fallbackKey: string,
) =>
  nullableNumber(
    hasOwn(source, preferredKey) ? source[preferredKey] : source[fallbackKey],
  );

const stageValue = (
  stage: IOrderOperationsStage | undefined,
  extraKey: string,
  stageKey: keyof IOrderOperationsStage,
) => {
  const metrics = extra(stage);
  return nullableNumber(
    hasOwn(metrics, extraKey) ? metrics[extraKey] : stage?.[stageKey],
  );
};

const sourceRows = <TRow extends Record<string, any>>(
  stage: IOrderOperationsStage | undefined,
  extraKey: string,
): TRow[] => {
  const value = extra(stage)[extraKey];
  if (Array.isArray(value)) return value;
  return ((stage?.areas || []) as unknown) as TRow[];
};

const outsideArea = <TRow extends Record<string, any>>(
  stage: IOrderOperationsStage | undefined,
) => {
  const value = extra(stage).outsideAreas;
  return value && typeof value === "object" ? (value as TRow) : undefined;
};

const orderCount = (row: Record<string, any>) =>
  countNumber(row.orderCount ?? row.ordersCount);

const mapStoreAcceptanceArea = (
  row: IOrderOperationsAreaStat | Record<string, any>,
  index = 0,
): IStoreAcceptanceTimeStat => ({
  areaId: row.areaId ?? null,
  areaName: row.areaName ?? "",
  orderCount: orderCount(row),
  averageAcceptanceMinutes: metricValue(
    row,
    "averageAcceptanceMinutes",
    "averageMinutes",
  ),
  medianAcceptanceMinutes: metricValue(
    row,
    "medianAcceptanceMinutes",
    "medianMinutes",
  ),
  minAcceptanceMinutes: metricValue(row, "minAcceptanceMinutes", "minMinutes"),
  maxAcceptanceMinutes: metricValue(row, "maxAcceptanceMinutes", "maxMinutes"),
});

const mapPreparingArea = (
  row: IOrderOperationsAreaStat | Record<string, any>,
): IPreparingTimeAreaStat => ({
  areaId: row.areaId ?? null,
  areaName: row.areaName ?? "",
  orderCount: orderCount(row),
  averagePreparingMinutes: metricValue(
    row,
    "averagePreparingMinutes",
    "averageMinutes",
  ),
  medianPreparingMinutes: metricValue(
    row,
    "medianPreparingMinutes",
    "medianMinutes",
  ),
  p90PreparingMinutes: metricValue(row, "p90PreparingMinutes", "p90Minutes"),
  customerAcceptedRate: nullableNumber(row.customerAcceptedRate),
  unavailableProductOrderRate: nullableNumber(row.unavailableProductOrderRate),
});

const mapCustomerAcceptanceArea = (
  row: IOrderOperationsAreaStat | Record<string, any>,
): IModifiedOrderAcceptanceTimeAreaStat => ({
  areaId: row.areaId ?? null,
  areaName: row.areaName ?? "",
  orderCount: orderCount(row),
  averageAcceptanceMinutes: metricValue(
    row,
    "averageAcceptanceMinutes",
    "averageMinutes",
  ),
  medianAcceptanceMinutes: metricValue(
    row,
    "medianAcceptanceMinutes",
    "medianMinutes",
  ),
  p90AcceptanceMinutes: metricValue(row, "p90AcceptanceMinutes", "p90Minutes"),
  outlierCount: countNumber(row.outlierCount),
  outlierRate: nullableNumber(row.outlierRate),
  nearTimeoutOrderCount: countNumber(row.nearTimeoutOrderCount),
  nearTimeoutOrderRate: nullableNumber(row.nearTimeoutOrderRate),
  unavailableProductOrderRate: nullableNumber(row.unavailableProductOrderRate),
});

const mapAccountingGroup = (
  row: IOrderOperationsAreaStat | Record<string, any>,
): IAccountingFinalizationGroupStat => ({
  orderCount: orderCount(row),
  averageAccountingMinutes: metricValue(
    row,
    "averageAccountingMinutes",
    "averageMinutes",
  ),
  medianAccountingMinutes: metricValue(
    row,
    "medianAccountingMinutes",
    "medianMinutes",
  ),
  p90AccountingMinutes: metricValue(row, "p90AccountingMinutes", "p90Minutes"),
  p95AccountingMinutes: metricValue(row, "p95AccountingMinutes", "p95Minutes"),
  maxAccountingMinutes: metricValue(row, "maxAccountingMinutes", "maxMinutes"),
});

const mapDriverSearchGroup = (
  row: IOrderOperationsAreaStat | Record<string, any>,
): IDriverSearchGroupStat => ({
  orderCount: orderCount(row),
  averageDriverSearchMinutes: metricValue(
    row,
    "averageDriverSearchMinutes",
    "averageMinutes",
  ),
  medianDriverSearchMinutes: metricValue(
    row,
    "medianDriverSearchMinutes",
    "medianMinutes",
  ),
  p90DriverSearchMinutes: metricValue(
    row,
    "p90DriverSearchMinutes",
    "p90Minutes",
  ),
  p95DriverSearchMinutes: metricValue(
    row,
    "p95DriverSearchMinutes",
    "p95Minutes",
  ),
  maxDriverSearchMinutes: metricValue(
    row,
    "maxDriverSearchMinutes",
    "maxMinutes",
  ),
  driversNotifiedCount: countNumber(row.driversNotifiedCount),
  driverAcceptedCount: countNumber(row.driverAcceptedCount),
  driverRejectedCount: countNumber(row.driverRejectedCount),
  driverNoAnswerCount: countNumber(row.driverNoAnswerCount),
  tripAssignedCount: countNumber(row.tripAssignedCount),
  repeatedDriverSearchCount: countNumber(row.repeatedDriverSearchCount),
});

const mapDeliveryArea = (
  row: IOrderOperationsAreaStat | Record<string, any>,
): IAreaDeliveryTimeStat => ({
  areaId: row.areaId ?? null,
  areaName: row.areaName ?? "",
  orderCount: orderCount(row),
  averageDeliveryMinutes: metricValue(
    row,
    "averageDeliveryMinutes",
    "averageMinutes",
  ),
  medianDeliveryMinutes: metricValue(
    row,
    "medianDeliveryMinutes",
    "medianMinutes",
  ),
  minDeliveryMinutes: metricValue(row, "minDeliveryMinutes", "minMinutes"),
  maxDeliveryMinutes: metricValue(row, "maxDeliveryMinutes", "maxMinutes"),
});

const normalizeDataQuality = (
  value:
    | IOrderOperationsDataQualityStat[]
    | Record<string, any>
    | undefined,
): IOrderCycleTimeDataQualityStat[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item, index) => ({
      reasonKey: item.reasonKey ?? item.key ?? `reason-${index}`,
      reason: item.reason ?? item.label ?? item.reasonKey ?? "-",
      ordersCount: countNumber(item.ordersCount ?? item.orderCount ?? item.count),
    }));
  }

  return Object.entries(value).map(([key, item]) => {
    const itemValue = item && typeof item === "object" ? item : {};
    return {
      reasonKey: key,
      reason: itemValue.reason ?? itemValue.label ?? key,
      ordersCount: countNumber(
        itemValue.ordersCount ?? itemValue.orderCount ?? itemValue.count ?? item,
      ),
    };
  });
};

const withCancellationPeriod = (
  stats: ICancellationStats | undefined,
  startDate: string,
  endDate: string,
) =>
  stats
    ? {
        ...stats,
        startDate: stats.startDate ?? startDate,
        endDate: stats.endDate ?? endDate,
      }
    : undefined;

export const mapOrderOperationsAnalytics = (
  analytics?: IOrderOperationsAnalytics,
): IOrderOperationsMappedStats => {
  if (!analytics) return {};

  const { startDate, endDate } = analytics;
  const stages = analytics.lifecycle?.stages;
  const storeAcceptance = stages?.storeAcceptance;
  const preparing = stages?.preparing;
  const customerAcceptance = stages?.customerAcceptance;
  const accountingFinalization = stages?.accountingFinalization;
  const driverSearch = stages?.driverSearch;
  const delivery = stages?.delivery;
  const orderCycle = stages?.orderCycle;

  return {
    storeAcceptance: storeAcceptance
      ? {
          startDate,
          endDate,
          includedOrderCount: storeAcceptance.includedOrderCount,
          excludedOrderCount: storeAcceptance.excludedOrderCount,
          averageAcceptanceMinutes: stageValue(
            storeAcceptance,
            "averageAcceptanceMinutes",
            "averageMinutes",
          ),
          areas: sourceRows(storeAcceptance, "areas").map(mapStoreAcceptanceArea),
          outsideAreas: outsideArea(storeAcceptance)
            ? mapStoreAcceptanceArea(outsideArea(storeAcceptance)!)
            : undefined,
        }
      : undefined,
    preparing: preparing
      ? {
          startDate,
          endDate,
          includedOrderCount: preparing.includedOrderCount,
          excludedOrderCount: preparing.excludedOrderCount,
          averagePreparingMinutes: stageValue(
            preparing,
            "averagePreparingMinutes",
            "averageMinutes",
          ),
          medianPreparingMinutes: stageValue(
            preparing,
            "medianPreparingMinutes",
            "medianMinutes",
          ),
          p90PreparingMinutes: stageValue(preparing, "p90PreparingMinutes", "p90Minutes"),
          directToDriverCount: countNumber(extra(preparing).directToDriverCount),
          directToDriverRate: nullableNumber(extra(preparing).directToDriverRate),
          customerAcceptedCount: countNumber(extra(preparing).customerAcceptedCount),
          customerAcceptedRate: nullableNumber(extra(preparing).customerAcceptedRate),
          unavailableProductOrderCount: countNumber(
            extra(preparing).unavailableProductOrderCount,
          ),
          unavailableProductOrderRate: nullableNumber(
            extra(preparing).unavailableProductOrderRate,
          ),
          unavailableProductCount: countNumber(extra(preparing).unavailableProductCount),
          shortageValue: nullableNumber(extra(preparing).shortageValue),
          stores: extra(preparing).stores || [],
          areas: sourceRows(preparing, "areas").map(mapPreparingArea),
          outsideAreas: outsideArea(preparing)
            ? mapPreparingArea(outsideArea(preparing)!)
            : undefined,
        }
      : undefined,
    customerAcceptance: customerAcceptance
      ? {
          startDate,
          endDate,
          includedOrderCount: customerAcceptance.includedOrderCount,
          excludedOrderCount: customerAcceptance.excludedOrderCount,
          averageAcceptanceMinutes: stageValue(
            customerAcceptance,
            "averageAcceptanceMinutes",
            "averageMinutes",
          ),
          medianAcceptanceMinutes: stageValue(
            customerAcceptance,
            "medianAcceptanceMinutes",
            "medianMinutes",
          ),
          p90AcceptanceMinutes: stageValue(
            customerAcceptance,
            "p90AcceptanceMinutes",
            "p90Minutes",
          ),
          outlierCount: customerAcceptance.outlierCount,
          outlierRate: customerAcceptance.outlierRate,
          nearTimeoutOrderCount: countNumber(extra(customerAcceptance).nearTimeoutOrderCount),
          nearTimeoutOrderRate: nullableNumber(
            extra(customerAcceptance).nearTimeoutOrderRate,
          ),
          repeatedCustomerAcceptanceCount: countNumber(
            extra(customerAcceptance).repeatedCustomerAcceptanceCount,
          ),
          repeatedCustomerAcceptanceRate: nullableNumber(
            extra(customerAcceptance).repeatedCustomerAcceptanceRate,
          ),
          unavailableProductOrderCount: countNumber(
            extra(customerAcceptance).unavailableProductOrderCount,
          ),
          unavailableProductOrderRate: nullableNumber(
            extra(customerAcceptance).unavailableProductOrderRate,
          ),
          unavailableProductCount: countNumber(
            extra(customerAcceptance).unavailableProductCount,
          ),
          partialShortageProductCount: countNumber(
            extra(customerAcceptance).partialShortageProductCount,
          ),
          fullMissingProductCount: countNumber(
            extra(customerAcceptance).fullMissingProductCount,
          ),
          valueReduction: nullableNumber(extra(customerAcceptance).valueReduction),
          missingModifiedProductContextCount: countNumber(
            extra(customerAcceptance).missingModifiedProductContextCount,
          ),
          datetimeSendClientFallbackCount: countNumber(
            extra(customerAcceptance).datetimeSendClientFallbackCount,
          ),
          stores: extra(customerAcceptance).stores || [],
          areas: sourceRows(customerAcceptance, "areas").map(mapCustomerAcceptanceArea),
          outsideAreas: outsideArea(customerAcceptance)
            ? mapCustomerAcceptanceArea(outsideArea(customerAcceptance)!)
            : undefined,
        }
      : undefined,
    accountingFinalization: accountingFinalization
      ? {
          startDate,
          endDate,
          candidateCompletedOrderCount: countNumber(
            extra(accountingFinalization).candidateCompletedOrderCount,
          ),
          trackedAccountingOrderCount: countNumber(
            extra(accountingFinalization).trackedAccountingOrderCount ??
              accountingFinalization.includedOrderCount,
          ),
          mergedWithDriverSearchCount: countNumber(
            extra(accountingFinalization).mergedWithDriverSearchCount,
          ),
          excludedOrderCount: accountingFinalization.excludedOrderCount,
          averageAccountingMinutes: stageValue(
            accountingFinalization,
            "averageAccountingMinutes",
            "averageMinutes",
          ),
          medianAccountingMinutes: stageValue(
            accountingFinalization,
            "medianAccountingMinutes",
            "medianMinutes",
          ),
          p90AccountingMinutes: stageValue(
            accountingFinalization,
            "p90AccountingMinutes",
            "p90Minutes",
          ),
          p95AccountingMinutes: stageValue(
            accountingFinalization,
            "p95AccountingMinutes",
            "p95Minutes",
          ),
          missingAccountingFinishedByCount: countNumber(
            extra(accountingFinalization).missingAccountingFinishedByCount,
          ),
          missingAccountingFinishedAtCount: countNumber(
            extra(accountingFinalization).missingAccountingFinishedAtCount,
          ),
          accountants: (extra(accountingFinalization).accountants || []).map(
            (row: Record<string, any>) => ({
              accountantId: row.accountantId ?? null,
              accountantName: row.accountantName ?? "",
              ...mapAccountingGroup(row),
            }),
          ),
          areas: sourceRows(accountingFinalization, "areas").map((row) => ({
            areaId: row.areaId ?? null,
            areaName: row.areaName ?? "",
            ...mapAccountingGroup(row),
          })),
          stores: extra(accountingFinalization).stores || [],
          daily: extra(accountingFinalization).daily || [],
        }
      : undefined,
    driverSearch: driverSearch
      ? {
          startDate,
          endDate,
          candidateCompletedOrderCount: countNumber(
            extra(driverSearch).candidateCompletedOrderCount,
          ),
          includedOrderCount: driverSearch.includedOrderCount,
          excludedOrderCount: driverSearch.excludedOrderCount,
          missingDriverSearchStatusCount: countNumber(
            extra(driverSearch).missingDriverSearchStatusCount,
          ),
          missingAssignmentAfterDriverSearchCount: countNumber(
            extra(driverSearch).missingAssignmentAfterDriverSearchCount,
          ),
          missingCompletedStatusHistoryCount: countNumber(
            extra(driverSearch).missingCompletedStatusHistoryCount,
          ),
          invalidDurationCount: countNumber(extra(driverSearch).invalidDurationCount),
          legacyCombinedCount: countNumber(extra(driverSearch).legacyCombinedCount),
          trackedDriverSearchCount: countNumber(
            extra(driverSearch).trackedDriverSearchCount,
          ),
          averageDriverSearchMinutes: stageValue(
            driverSearch,
            "averageDriverSearchMinutes",
            "averageMinutes",
          ),
          medianDriverSearchMinutes: stageValue(
            driverSearch,
            "medianDriverSearchMinutes",
            "medianMinutes",
          ),
          p90DriverSearchMinutes: stageValue(
            driverSearch,
            "p90DriverSearchMinutes",
            "p90Minutes",
          ),
          p95DriverSearchMinutes: stageValue(
            driverSearch,
            "p95DriverSearchMinutes",
            "p95Minutes",
          ),
          legacyCombined: extra(driverSearch).legacyCombined || mapDriverSearchGroup({}),
          trackedDriverSearch:
            extra(driverSearch).trackedDriverSearch || mapDriverSearchGroup({}),
          driverNotificationCount: countNumber(
            extra(driverSearch).driverNotificationCount,
          ),
          driverAcceptedCount: countNumber(extra(driverSearch).driverAcceptedCount),
          driverRejectedCount: countNumber(extra(driverSearch).driverRejectedCount),
          driverNoAnswerCount: countNumber(extra(driverSearch).driverNoAnswerCount),
          tripAssignedCount: countNumber(extra(driverSearch).tripAssignedCount),
          legacyDriverAcceptedCount: countNumber(
            extra(driverSearch).legacyDriverAcceptedCount,
          ),
          enRouteFallbackCount: countNumber(extra(driverSearch).enRouteFallbackCount),
          repeatedDriverSearchCount: countNumber(
            extra(driverSearch).repeatedDriverSearchCount,
          ),
          dataQualityReasons: extra(driverSearch).dataQualityReasons || [],
          areas: sourceRows(driverSearch, "areas").map((row) => ({
            areaId: row.areaId ?? null,
            areaName: row.areaName ?? "",
            ...mapDriverSearchGroup(row),
          })),
          stores: extra(driverSearch).stores || [],
          daily: extra(driverSearch).daily || [],
        }
      : undefined,
    delivery: delivery
      ? {
          startDate,
          endDate,
          includedOrderCount: delivery.includedOrderCount,
          excludedOrderCount: delivery.excludedOrderCount,
          averageDeliveryMinutes: stageValue(
            delivery,
            "averageDeliveryMinutes",
            "averageMinutes",
          ),
          areas: sourceRows(delivery, "areas").map(mapDeliveryArea),
          outsideAreas: outsideArea(delivery)
            ? mapDeliveryArea(outsideArea(delivery)!)
            : undefined,
        }
      : undefined,
    orderCycle: orderCycle
      ? {
          reportKey: "orderCycleTime",
          reportName: extra(orderCycle).reportName ?? orderCycle.label,
          startDate,
          endDate,
          candidateCompletedOrderCount: countNumber(
            extra(orderCycle).candidateCompletedOrderCount,
          ),
          includedOrderCount: orderCycle.includedOrderCount,
          excludedOrderCount: orderCycle.excludedOrderCount,
          averageCycleMinutes: stageValue(orderCycle, "averageCycleMinutes", "averageMinutes"),
          medianCycleMinutes: stageValue(orderCycle, "medianCycleMinutes", "medianMinutes"),
          p75CycleMinutes: orderCycle.p75Minutes,
          p90CycleMinutes: stageValue(orderCycle, "p90CycleMinutes", "p90Minutes"),
          p95CycleMinutes: stageValue(orderCycle, "p95CycleMinutes", "p95Minutes"),
          maxCycleMinutes: stageValue(orderCycle, "maxCycleMinutes", "maxMinutes"),
          outlierCount: orderCycle.outlierCount,
          outlierRate: orderCycle.outlierRate,
          datetimeStartOrderMissingCount: countNumber(
            extra(orderCycle).datetimeStartOrderMissingCount,
          ),
          repeatedCompletedStatusCount: countNumber(
            extra(orderCycle).repeatedCompletedStatusCount,
          ),
          stageCoverageRate: nullableNumber(
            hasOwn(extra(orderCycle), "stageCoverageRate")
              ? extra(orderCycle).stageCoverageRate
              : analytics.lifecycle?.summary?.stageCoverageRate,
          ),
          stageSumGreaterThanTotalCount: countNumber(
            extra(orderCycle).stageSumGreaterThanTotalCount,
          ),
          areas: sourceRows(orderCycle, "areas").map((row) => ({
            areaId: row.areaId ?? null,
            areaName: row.areaName ?? "",
            ordersCount: countNumber(row.ordersCount ?? row.orderCount),
            averageCycleMinutes: metricValue(
              row,
              "averageCycleMinutes",
              "averageMinutes",
            ),
            medianCycleMinutes: metricValue(
              row,
              "medianCycleMinutes",
              "medianMinutes",
            ),
            p90CycleMinutes: metricValue(row, "p90CycleMinutes", "p90Minutes"),
            p95CycleMinutes: metricValue(row, "p95CycleMinutes", "p95Minutes"),
            outlierCount: countNumber(row.outlierCount),
            stageCoverageRate: nullableNumber(row.stageCoverageRate),
          })),
          bucketDistribution:
            extra(orderCycle).bucketDistribution ||
            analytics.lifecycle?.bucketDistribution ||
            [],
          phaseBreakdown:
            extra(orderCycle).phaseBreakdown || analytics.lifecycle?.phaseBreakdown || [],
          dataQuality: normalizeDataQuality(
            extra(orderCycle).dataQuality ||
              orderCycle.dataQuality ||
              analytics.lifecycle?.dataQualitySummary,
          ),
        }
      : undefined,
    customerCancellation: withCancellationPeriod(
      analytics.cancellations?.customerCancellation,
      startDate,
      endDate,
    ),
    administrativeCancellation: withCancellationPeriod(
      analytics.cancellations?.administrativeCancellation,
      startDate,
      endDate,
    ),
  };
};
